import React, { useEffect, useState } from 'react';
import { Button, Message, Header, Input, useFrontier, Modal, Popup } from 'react-frontier2';
import { Seatmap } from '@arema/components';
import { VenueSeat, SeatStatus, SectionSeatmap, SeatmapSeat } from '@arema/components/Classes';
import { bindClick, formatSeatNumber, seatStatusLocale } from '@arema/components/Util';
import { Link } from 'react-router-dom';
import Validator from '@arema/components/Validator';
import classNames from 'classnames';
import API from '../API';

import '@arema/components/style/seatmap.scss';

interface SeatmapProps {
	dateId: number,
	sectionId: number,
}
enum ModalType {
	SEAT_COMMENT = 1,
	SEAT_COST = 2,
}

var SeatmapC = (props: SeatmapProps) => {
	var { Toast } = useFrontier();
	var [selected, setSelected] = useState([]);
	var [seatmap, setSeatmap] = useState<SectionSeatmap>(null);
	var [sending, setSending] = useState<boolean>(false);
	var [loadError, setLoadError] = useState<string>(null);
	var [shownModal, setShownModal] = useState<ModalType>(null);
	var [seatComment, setSeatComment] = useState<string>('');
	var [seatCost, setSeatCost] = useState<number>(null);
	var [seatCommission, setSeatCommission] = useState<number>(null);
	var [seatStatusSelected, setSeatStatusSelected] = useState<number>(null);

	useEffect(() => {
		setSelected([]);
		API.getDateSeatmap(props.dateId, props.sectionId).then(res => {
			if (res.error) return setLoadError(res.message);
			setSeatmap(res.data);
		}).catch(err => {
			return setLoadError('Hubo un error inesperado cargando el mapa de butacas (LCL-1)');
		})
	}, [props.sectionId, props.dateId]);

	if (!seatmap || loadError) {
		return <div style={{ height: 600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
			<Header loading={!loadError} text={loadError || 'Cargando mapa...'} iconName={loadError ? 'face-frown-open' : null} size={loadError ? 'small' : null} />
		</div>;
	}

	var map: SeatmapSeat[][] = [];
	for (let y = 0; y < seatmap.seats_height; y++) {
		var row: SeatmapSeat[] = [];
		for (let x = 0; x < seatmap.seats_width; x++) {
			var seat = seatmap.seats.find(a => a.seat_x === x && a.seat_y === y);
			row.push(seat || null);
		}
		map.push(row);
	}

	var onSeatClick = (seat_id: number) => {
		return () => {
			if (sending) return;
			var sel = [...selected];
			var ix = sel.indexOf(seat_id);
			if (ix === -1) {
				sel.push(seat_id);
			} else {
				sel = sel.filter(a => a !== seat_id);
			}
			setSelected(sel);
		}
	}

	var save = (seat_status?: number) => {
		return () => {
			if (!selected || selected.length === 0) return;
			setSending(true);
			const status = seat_status ? seat_status : seatStatusSelected;
			API.setDateSeatStatus(props.dateId, status, selected, seatComment).then(res => {
				if (res.error) {
					return Toast.error(res.message);
				}
				var sm = { ...seatmap };
				for (let i of selected) {
					var ix = sm.seats.findIndex(a => a.seat_id === i);
					if (ix === -1) continue;
					sm.seats[ix].seat_status = status === -1 ? sm.seats[ix].venue_seat_status : status;
					sm.seats[ix].status_comment = seatComment ? seatComment : '';
				}
				setSelected([]);
				setSeatmap(sm);
				setShownModal(null);
				Toast.success('Se ha guardado el estatus de las butacas');
			}).catch(err => {
				return Toast.error('Hubo un error inesperado cambiando el estatus de las butacas.');
			}).finally(() => {
				setSending(false);
			})
		}
	}

	var saveCost = () => {
		return () => {
			if (!selected || selected.length === 0) return;
			
			var { valid } = Validator({ cost: seatCost, commission: seatCommission }, {
				cost: [{ rule: 'number' }, { rule: 'min', params: [1] }],
				commission: [{ rule: 'number' }, { rule: 'min', params: [1] }],
			});
			if (!valid) return Toast.error('El costo tiene que ser un numero');
			
			setSending(true);
			API.setDateSeatCost(props.dateId, seatCost, seatCommission, selected).then(res => {
				if (res.error) {
					return Toast.error(res.message);
				}
				var sm = { ...seatmap };
				for (let i of selected) {
					var ix = sm.seats.findIndex(a => a.seat_id === i);
					if (ix === -1) continue;
					sm.seats[ix].seat_cost = seatCost ? seatCost : null;
					sm.seats[ix].seat_commission = seatCommission ? seatCommission : null;
				}
				setSelected([]);
				setSeatCost(null);
				setSeatCommission(null);
				setShownModal(null);
				Toast.success('Se ha guardado el costo de las butacas');
			}).catch(err => {
				return Toast.error('Hubo un error inesperado cambiando el costo de las butacas.');
			}).finally(() => {
				setSending(false);
			})
		}
	}

	const openCommentSeatModal = (status_id: number) => {
		return () => {
			setSeatStatusSelected(status_id)
			if (status_id !== SeatStatus.FREE) {
				setSeatComment('');
				setShownModal(ModalType.SEAT_COMMENT);
			} else {
				save(status_id)();
			}
		}
	}

	var has_stage = seatmap && seatmap.seats_height > 0 && seatmap.seats_width > 0 && seatmap.stage_x !== null && seatmap.stage_y !== null;
	var seat_style = {
		width: 30,
		height: 30,
		fontSize: 10,
		borderRadius: 6,
		text: false,
	}

	return <>
		<div className="fr tooled seatmap">
			<div className="toolbar">
				{selected.length === 0 ? (
					<Message type='info' text={'Selecciona butacas para modificar su estado'} style={{ fontSize: 14, padding: '10px 20px' }} />
				) : <>
					<Button text='Deseleccionar' disabled={!!sending} onClick={bindClick(setSelected, [])} />
					<Button text='Cambiar estado' menu={[
						{ text: "Heredar", iconName: "unlink", onClick: openCommentSeatModal(-1) },
						{ text: "Normal", iconName: "ticket", onClick: openCommentSeatModal(SeatStatus.FREE) },
						{ text: "Bloquear", iconName: "ban", onClick: openCommentSeatModal(SeatStatus.BLOCKED) },
						{ text: "Candado", iconName: "lock", onClick: openCommentSeatModal(SeatStatus.LOCKED) },
						{ text: "Esconder", iconName: "eye-slash", onClick: openCommentSeatModal(SeatStatus.HIDDEN) },
						{ text: "Solo PDV", iconName: "cash-register", onClick: openCommentSeatModal(SeatStatus.FREE_PDV) },
					]} />
					<Button text='Asignar Precio' onClick={bindClick(setShownModal, ModalType.SEAT_COST)} />
				</>}
			</div>
			<Seatmap className="seats" style={{ marginTop: 60 }}>
				{map.map((row, i) => (
					<Seatmap.Row seatStyle={seat_style} className="row"  key={`smprw-${i}`}>
						{row.map((seat, si) => {
							var key = `smpst-${i}-${si}`
							var is_stage = has_stage && seatmap.stage_x <= si && (seatmap.stage_x + seatmap.stage_width) > si && seatmap.stage_y <= i && (seatmap.stage_y + seatmap.stage_height) > i;
							if (is_stage) {
								if(si!=seatmap.stage_x) return null;
								var stage_row = i-seatmap.stage_y;
								var is_center = Math.max(Math.min(Math.floor(seatmap.stage_height/2), seatmap.stage_height), 0)==stage_row;
								var is_top = i==seatmap.stage_y, is_bottom = i==(seatmap.stage_y+seatmap.stage_height-1);

								return <Seatmap.Seat key={key} stage={[
									is_top ? 1 : 0, is_top ? 1 : 0,
									is_bottom ? 1 : 0, is_bottom ? 1 : 0,
								]} size={seatmap.stage_width} seatStyle={seat_style} style={{
									marginTop: is_top ? 2 : 0,
									marginLeft: 0,
									marginRight: 0
								}}>
									{is_center ? 'Escenario' : null}
								</Seatmap.Seat>
							} else if (!seat) {
								return <Seatmap.Seat seatStyle={seat_style} empty key={key} />
							}

							return <Popup
								openOnHover
								trigger={(
									<Seatmap.Seat
										seatStyle={seat_style}
										color={![SeatStatus.FREE, SeatStatus.FREE_PDV].includes(seat.seat_status) ? 'red' : SeatStatus.FREE_PDV == seat.seat_status ? 'light-red' : (selected.indexOf(seat.seat_id) > -1 ? 'orange' : (seat.ticket_id ? 'green' : 'gray'))}
										subtext={seat.seat_cost ? `$${parseFloat(`${seat.seat_cost}`) + parseFloat(`${(seat.seat_commission ? seat.seat_commission : 0)}`) }` : seat.seat_section}
										onClick={onSeatClick(seat.seat_id)} >
										<div className="number">
											{seat.seat_status !== SeatStatus.FREE ? (
												<i className={classNames('icon', {
													ban: seat.seat_status === SeatStatus.BLOCKED,
													lock: seat.seat_status === SeatStatus.LOCKED,
													'eye slash': seat.seat_status === SeatStatus.HIDDEN,
													'cash register': seat.seat_status === SeatStatus.FREE_PDV,
												})}></i>
											) : null}
											{formatSeatNumber(seat.seat_row, seat.seat_number)}
										</div>
									</Seatmap.Seat>
								)}
								key={key}
								style={{ padding: 0, borderRadius: 12 }}
							>
								<table className="fr striped details basic table">
									<tbody>
										<tr>
											<td>ID</td>
											<td>{seat.seat_id}</td>
										</tr>
										<tr>
											<td>Estatus</td>
											<td>{seat.ticket_id ? 'Vendida' : seatStatusLocale(seat.seat_status)}</td>
										</tr>
										{seat.venue_seat_status && seat.venue_seat_status !== 1 && seat.venue_seat_status !== seat.seat_status ? (
											<tr>
												<td>Estatus Recinto</td>
												<td>{seatStatusLocale(seat.venue_seat_status)}</td>
											</tr>
										) : null}
										{seat.ticket_id ? (
											<tr>
												<td>Boleto</td>
												<td>{seat.ticket_id}</td>
											</tr>
										) : null}
										{seat.order_id ? (
											<tr>
												<td>Orden</td>
												<td>
													<Link className='normal' to={`/orders/@${seat.order_id}`} target='_blank'>{seat.order_id}</Link>
												</td>
											</tr>
										) : null}
										<tr>
											<td>Fila</td>
											<td>{seat.seat_row}</td>
										</tr>
										<tr>
											<td>Butaca</td>
											<td>{seat.seat_number}</td>
										</tr>
										{seat.seat_section && seat.seat_section.length > 0 ? (
											<tr>
												<td>Sección</td>
												<td>{seat.seat_section}</td>
											</tr>
										) : null}
										<tr>
											<td>Posición</td>
											<td>({seat.seat_x}, {seat.seat_y})</td>
										</tr>
										{!!seat.status_comment && seat.status_comment.length > 0 && (
											<tr>
												<td>Comentario</td>
												<td>{seat.status_comment}</td>
											</tr>
										)}
									</tbody>
								</table>
							</Popup>
						})}
					</Seatmap.Row>
				))}
			</Seatmap>
		</div>
		<Modal open={shownModal === ModalType.SEAT_COMMENT} onClose={bindClick(setShownModal)} size="mini">
			<Modal.Header>Cambio de estatus</Modal.Header>
			<Modal.Content>
				<Header size='small' text='¿Porque estas cambiando de estado las butacas?' />
				<Input
					value={seatComment}
					onChange={setSeatComment}
					label='Comentario'
					comment={'Razón del cambio de estado de las butacas'}
				/>
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic onClick={bindClick(setShownModal)} />
				<Button text='Guardar' color={'black'} loading={sending} onClick={save(seatStatusSelected)} />
			</Modal.Actions>
		</Modal>
		
		<Modal open={shownModal === ModalType.SEAT_COST} onClose={bindClick(setShownModal)} size="mini">
			<Modal.Header>Cambio de precio</Modal.Header>
			<Modal.Content>
				<Header size='small' text='¿Precio de las butacas?' />
				<Input
					value={seatCost}
					onChange={setSeatCost}
					label='Costo'
					inputType='number'
				/>
				<Input
					value={seatCommission}
					onChange={setSeatCommission}
					label='Comisión'
					inputType='number'
				/>
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic onClick={bindClick(setShownModal)} />
				<Button text='Guardar' color={'black'} loading={sending} onClick={saveCost()} />
			</Modal.Actions>
		</Modal>
	</>
}

export default SeatmapC;